/**
 * ChartWidget type definitions
 * P0 + P1 types: basic chart configuration and data shapes
 */

export type ChartVisualType = 'line' | 'area' | 'bar' | 'hbar' | 'pie' | 'doughnut' | 'radar' | 'funnel' | 'radialGauge';

/** Supported Chart.js interaction modes. */
export type InteractionMode = 'point' | 'nearest' | 'index';

/** Default interaction mode — matches Chart.js built-in default. Change here to update everywhere. */
export const DEFAULT_INTERACTION_MODE: InteractionMode = 'nearest';

/** Per-chart interaction configuration (tooltip/hover behavior). */
export interface ChartInteractionConfig {
  mode?: InteractionMode;
  intersect?: boolean;
}

// P0: only label + color. Specific options (tension, borderRadius, stepped) are P1.
export interface ChartSeriesConfig {
  fieldName: string;
  label: string;
  chartTypeOverride?: ChartVisualType;
  color?: string;
  showDataLabels?: boolean;
  /** @deprecated Moved to ChartConfig — kept for backward compat with existing data */
  funnelOrientation?: 'horizontal' | 'vertical';
  // Per-series formatting settings
  prefix?: string;
  suffix?: string;
  useMillions?: boolean;
  millionsFrom?: number | null;
  decimalPlaces?: number | null;
  showPoints?: boolean;    // Показывать точки на line/area (дефолт: true)
  pointRadius?: number;    // Радиус точек (0-20, дефолт: 3, игнорируется если showPoints=false)
}

/** Legend position for Chart.js legend plugin. */
export type LegendPosition = 'top' | 'bottom' | 'left' | 'right';

export interface ChartConfig {
  title?: string;
  description?: string;
  chartType: ChartVisualType;
  showLegend?: boolean;
  legendPosition?: LegendPosition;
  showAxes?: boolean;
  xAxisLabel?: string;
  yAxisLabel?: string;
  interactions?: ChartInteractionConfig;
  funnelOrientation?: 'horizontal' | 'vertical';
  funnelDataLabelFontSize?: number;
  funnelDataLabelColor?: string;
  funnelScale?: 'linear' | 'sqrt' | 'cbrt' | 'log';
  series: ChartSeriesConfig[];
}

export interface ChartSeriesData {
  name: string;
  type: ChartVisualType;
  color: string;
  data: (number | null)[];
  fieldName?: string;
  prefix?: string;
  suffix?: string;
  useMillions?: boolean;
  millionsFrom?: number;
  decimalPlaces?: number | null;
  perDataColors?: string[];
}

export interface ChartBackendData {
  series: ChartSeriesData[];
  categories: string[];
  error?: string;
}

/**
 * Chart panel data for the editor — stores formulas + API result.
 */
export interface ChartData {
  formulas?: Record<string, string>;
  series?: ChartSeriesData[];
  categories?: string[];
  error?: string;
}

/** Chart group: multiSeries (can mix types) or singleSeries (only 1 series). */
export type ChartGroup = 'multiSeries' | 'singleSeries';

/**
 * Maps each ChartVisualType to its group — single source of truth.
 *
 * @deprecated Use isOverrideAllowed(), isSegmented() and direct type selection instead.
 */
export const CHART_GROUP: Record<ChartVisualType, ChartGroup> = {
  line: 'multiSeries',
  area: 'multiSeries',
  bar: 'multiSeries',
  hbar: 'multiSeries',
  radar: 'multiSeries',
  pie: 'singleSeries',
  doughnut: 'singleSeries',
  funnel: 'multiSeries',
  radialGauge: 'singleSeries',
};

/** Types that support multiple series on one canvas. */
export const MULTI_SERIES_TYPES: readonly ChartVisualType[] = ['line', 'area', 'bar', 'hbar', 'radar'];

/** Types that support only a single series. */
export const SINGLE_SERIES_TYPES: readonly ChartVisualType[] = ['pie', 'doughnut', 'radialGauge'];

/**
 * Types that support mixed-series rendering (line, area, bar).
 * These types can be freely overridden between each other.
 */
export const MIXED_TYPES: readonly ChartVisualType[] = ['line', 'area', 'bar'];

/**
 * Types that render all data points as segments of a whole (funnel, pie, doughnut, radialGauge).
 * These types display proportional/segmented views rather than coordinate-based charts.
 */
export const SEGMENTED_TYPES: readonly ChartVisualType[] = ['funnel', 'pie', 'doughnut', 'radialGauge'];

/**
 * Solo chart types (hbar, radar) that have no override group and stand alone.
 */
export const SOLO_TYPES: readonly ChartVisualType[] = ['hbar', 'radar'];

/**
 * Returns the group for a given chart visual type.
 *
 * @deprecated Use isOverrideAllowed(), isSegmented() and direct type selection instead.
 */
export function getGroupForType(type: ChartVisualType): ChartGroup {
  return CHART_GROUP[type];
}

/**
 * Returns true if the chart type supports multiple series.
 *
 * @deprecated Use isOverrideAllowed(), isSegmented() and direct type selection instead.
 */
export function isMultiSeries(type: ChartVisualType): boolean {
  return CHART_GROUP[type] === 'multiSeries';
}

/**
 * Returns true if the chart type supports only a single series.
 *
 * @deprecated Use isOverrideAllowed(), isSegmented() and direct type selection instead.
 */
export function isSingleSeries(type: ChartVisualType): boolean {
  return CHART_GROUP[type] === 'singleSeries';
}

/**
 * Returns all chart visual types for a given group.
 *
 * @deprecated Use isOverrideAllowed(), isSegmented() and direct type selection instead.
 */
export function getTypesByGroup(group: ChartGroup): ChartVisualType[] {
  return group === 'multiSeries' ? [...MULTI_SERIES_TYPES] : [...SINGLE_SERIES_TYPES];
}

/**
 * Returns true if the given chart type supports chart type override (mixed rendering).
 * Only line, area, and bar can be freely overridden between each other.
 */
export function isOverrideAllowed(type: ChartVisualType): boolean {
  return (MIXED_TYPES as readonly ChartVisualType[]).includes(type);
}

/**
 * Returns true if the given chart type renders data points as segments (proportional views).
 * Segmented types: funnel, pie, doughnut, radialGauge.
 */
export function isSegmented(type: ChartVisualType): boolean {
  return (SEGMENTED_TYPES as readonly ChartVisualType[]).includes(type);
}
